import { ProgressBar } from 'react-aria-components';
import { cn } from './lib/cn';

export type SpinnerSize = 'sm' | 'md' | 'lg';

export type SpinnerProps = {
  /** Announced while the work runs. The mark itself stays unlabelled. */
  label?: string;
  size?: SpinnerSize;
  className?: string;
};

const SIZES: Record<SpinnerSize, string> = {
  sm: 'sk:size-3 sk:border-2',
  md: 'sk:size-5 sk:border-2',
  lg: 'sk:size-8 sk:border-[3px]',
};

/**
 * Indeterminate activity mark. Drawn in `currentColor`, so it takes the tone of
 * whatever it sits in rather than carrying a colour of its own.
 */
export function Spinner({
  label = 'Loading',
  size = 'md',
  className,
}: SpinnerProps) {
  return (
    <ProgressBar
      isIndeterminate
      aria-label={label}
      className={cn('sk:inline-flex', className)}
    >
      <span
        className={cn(
          'sk:block sk:animate-spin sk:rounded-full',
          'sk:border-current sk:border-t-transparent',
          SIZES[size],
        )}
      />
    </ProgressBar>
  );
}

export type LoadingOverlayProps = {
  /** Shown under the mark and announced. */
  label?: string;
  /*
   * Covers the viewport instead of the nearest positioned ancestor. Without it
   * the overlay needs a `relative` parent, or it will climb to the next one up.
   */
  fullScreen?: boolean;
  className?: string;
};

/** Veils what it covers while a save or a fetch is in flight. */
export function LoadingOverlay({
  label = 'Loading',
  fullScreen = false,
  className,
}: LoadingOverlayProps) {
  return (
    <div
      className={cn(
        'sk:inset-0 sk:flex sk:flex-col sk:items-center sk:justify-center sk:gap-3',
        'sk:bg-surface/75 sk:text-fg-secondary sk:backdrop-blur-xs',
        /* Matches Modal and Popover, which clear the legacy semantic layers. */
        fullScreen ? 'sk:fixed sk:z-3000' : 'sk:absolute sk:z-10',
        className,
      )}
    >
      <Spinner label={label} size="lg" className="sk:text-primary" />
      {label ? (
        <span className="sk:text-dense sk:font-medium sk:tracking-dense">
          {label}
        </span>
      ) : null}
    </div>
  );
}
